import i18next from "i18next";
import { initReactI18next } from "react-i18next";

import en from "./en.json";
import fr from "./fr.json";
import { setFormatLocale } from "../lib/format";

/**
 * Interface languages.
 *
 * English is the source: every key exists there, and `en.json` is what types the
 * catalogue, so a missing French string falls back to English rather than printing
 * a key at somebody. Adding a language means one more JSON file and one more entry
 * here.
 */
export const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "fr", label: "Français" },
] as const;

export type LanguageCode = (typeof LANGUAGES)[number]["code"];

const STORAGE_KEY = "lmustats.language";

/** A choice made in the UI beats the browser's, on this device only. */
function initialLanguage(): LanguageCode {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (isSupported(stored)) return stored;

  for (const tag of navigator.languages ?? [navigator.language]) {
    const base = tag?.split("-")[0]?.toLowerCase();
    if (isSupported(base)) return base;
  }
  return "en";
}

function isSupported(code: string | null | undefined): code is LanguageCode {
  return LANGUAGES.some((language) => language.code === code);
}

export function setLanguage(code: LanguageCode) {
  localStorage.setItem(STORAGE_KEY, code);
  void i18next.changeLanguage(code);
}

/**
 * Everything outside React that depends on the language: the document's own `lang`,
 * which decides how a screen reader pronounces the page and how the browser hyphenates
 * it, and the locale the number and date formatters use.
 */
function applyLanguage(code: string) {
  document.documentElement.lang = code;
  setFormatLocale(code);
}

void i18next.use(initReactI18next).init({
  resources: { en: { translation: en }, fr: { translation: fr } },
  lng: initialLanguage(),
  fallbackLng: "en",
  interpolation: {
    // React escapes for us, and double-escaping turns an apostrophe into &#39;.
    escapeValue: false,
  },
});

applyLanguage(i18next.language);
i18next.on("languageChanged", applyLanguage);

export default i18next;
